/**
 * The writes that need signal: they are never queued (unit 25, D8).
 *
 * Each is a verb whose effect depends on the moment it runs, or a call that is
 * a network call by nature. Replayed later, "build the shopping list" would
 * build from a pool and a list that have changed since the person pressed it
 * (25C3, Q2), and "copy the regulars" would copy them twice. The screen shows
 * these as needing signal (unit 26); `enqueue` refuses them, so a screen that
 * forgets cannot queue one.
 */

import type { Method } from "./outbox";

const NEEDS_SIGNAL: { methods: Method[]; path: RegExp; why: string }[] = [
  {
    methods: ["POST"],
    path: /^\/api\/meal-plan\/(regulars|staples|shopping-list(\/regulars)?)$/,
    why: "it builds a list from what is there when it runs",
  },
  { methods: ["POST"], path: /^\/api\/ocado\//, why: "it drives the shop's website" },
  {
    methods: ["POST", "PUT", "PATCH", "DELETE"],
    path: /^\/api\/notes\/tablet\//,
    why: "it reaches the tablet",
  },
  {
    methods: ["POST", "PATCH", "DELETE"],
    path: /^\/api\/recipes(\/\d+)?$/,
    why: "recipes are written online",
  },
  {
    methods: ["PATCH", "DELETE"],
    path: /^\/api\/calendar\/events\//,
    why: "an edit of a Google event goes to Google first (unit 38)",
  },
];

/** Why this write cannot be queued, or `null` if it can. */
export function needsSignal(method: Method, path: string): string | null {
  const found = NEEDS_SIGNAL.find((rule) => rule.methods.includes(method) && rule.path.test(path));
  return found?.why ?? null;
}
